/**
 * Deterministic review generation from scraped rating data.
 * Uses seeded PRNG (mulberry32) keyed by product handle for SSG/ISR stability.
 *
 * SHOP-83, SHOP-147
 */

export interface GeneratedReview {
  reviewerName: string
  rating: number
  date: string // ISO date string
  verified: boolean
  body: string // Review text — seeded per product+index
}

export interface ReviewData {
  averageRating: number
  totalReviews: number
  reviews: GeneratedReview[]
}

/** mulberry32 seeded PRNG */
function seededRandom(seed: number): () => number {
  return () => {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Hash a string to a 32-bit integer seed */
function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i)
    hash = ((hash << 5) - hash + c) | 0
  }
  return hash
}

const FIRST_NAMES = [
  "Alice", "Bob", "Charlie", "David", "Emma", "Fiona", "George", "Hannah",
  "Ian", "Julia", "Kevin", "Laura", "Michael", "Nina", "Oliver", "Penny",
  "Quinn", "Rachel", "Sam", "Tina", "Uma", "Victor", "Wendy", "Xander",
  "Yuki", "Zara", "Alex", "Beth", "Chris", "Diana", "Ethan", "Grace",
  "Harry", "Iris", "Jack", "Kate", "Liam", "Mia", "Noah", "Olivia",
  "Peter", "Rose", "Sarah", "Tom", "Vera", "Will", "Zoe", "Amy",
]

/** Generate an anonymous name like "A***e" or "M***l" */
function anonymizeName(rng: () => number): string {
  const name = FIRST_NAMES[Math.floor(rng() * FIRST_NAMES.length)]
  if (name.length <= 2) return name[0] + "***"
  return name[0] + "***" + name[name.length - 1]
}

/** Deterministic review body text pools by star rating */
const REVIEW_BODIES_5 = [
  "Absolutely love this product! My pet took to it immediately and the quality is excellent.",
  "Best purchase I've made for my pet. Highly recommended to all pet owners!",
  "Outstanding quality and fast delivery. My furry friend is obsessed with this!",
  "Exceeded all my expectations. Will definitely be purchasing again.",
  "Perfect in every way — my pet loves it and I love the quality.",
  "Five stars isn't enough! This product has been a game-changer for us.",
  "Super happy with this purchase. It arrived quickly and my pet approved immediately.",
  "Fantastic product, great value. My pet is much happier since we started using it.",
  "Amazing quality for the price. My vet even commented on how great it is!",
  "Could not be more satisfied. My pet's behaviour has improved noticeably.",
  "Absolutely worth every cent. My pet is thriving and I couldn't be happier.",
  "Wonderful product — solid construction, great design, and my pet loves it.",
]

const REVIEW_BODIES_4 = [
  "Really good product overall. My pet seems to enjoy it and the quality is solid.",
  "Happy with this purchase. Minor packaging issues but the product itself is great.",
  "Good value for money. My pet uses it every day without any complaints.",
  "Solid product, does what it says. Would recommend to other pet owners.",
  "Very pleased with this. Delivery was quick and my pet took to it right away.",
  "Great quality, though slightly smaller than I expected from the photos.",
  "My pet loves this. Lost one star only because the instructions could be clearer.",
  "A very good product. I would definitely buy again and recommend to friends.",
  "Works exactly as described. My pet is very happy and that's all that matters!",
  "Good purchase overall. The build quality is impressive for the price point.",
  "Pretty happy with it. Packaging was a bit rough but the item itself is great.",
  "Reliable and well made. My pet gave it four paws up!",
]

const REVIEW_BODIES_3 = [
  "Decent product but nothing special. My pet uses it but isn't super enthusiastic.",
  "Average quality. It does the job but I expected a bit more for the price.",
  "OK product. Some aspects are great, others could be improved.",
  "Fair value. My pet is indifferent — neither loves nor dislikes it.",
  "It works as advertised, just not as impressive as other reviewers suggested.",
  "Acceptable but not outstanding. Might look for alternatives next time.",
  "Middle of the road for me. The product is fine but customer service was slow.",
  "Good but not great. My pet uses it occasionally but doesn't seem excited.",
]

const REVIEW_BODIES_LOW = [
  "Unfortunately this didn't work for my pet. Returning and trying something else.",
  "Disappointed with the quality. Expected better for this price.",
  "My pet completely ignored this product. Not worth the money.",
  "Arrived damaged. Customer service was helpful but the product itself is poor.",
  "Not as described. The quality is much lower than the product photos suggest.",
]

/** Pick a seeded review body based on the star rating */
function generateReviewBody(rng: () => number, rating: number): string {
  if (rating >= 5) {
    return REVIEW_BODIES_5[Math.floor(rng() * REVIEW_BODIES_5.length)]
  } else if (rating >= 4) {
    return REVIEW_BODIES_4[Math.floor(rng() * REVIEW_BODIES_4.length)]
  } else if (rating >= 3) {
    return REVIEW_BODIES_3[Math.floor(rng() * REVIEW_BODIES_3.length)]
  } else {
    return REVIEW_BODIES_LOW[Math.floor(rng() * REVIEW_BODIES_LOW.length)]
  }
}

/**
 * Generate a rating distributed around the average.
 * Weighted toward 4-5 stars for high averages.
 */
function generateRating(rng: () => number, avg: number): number {
  // Use a simple approach: bias toward the average
  const r = rng()
  // Distribution weights based on average
  if (avg >= 4.0) {
    // Mostly 4-5 stars, occasional 3
    if (r < 0.45) return 5
    if (r < 0.82) return 4
    if (r < 0.94) return 3
    if (r < 0.98) return 2
    return 1
  } else if (avg >= 3.0) {
    if (r < 0.20) return 5
    if (r < 0.50) return 4
    if (r < 0.80) return 3
    if (r < 0.93) return 2
    return 1
  } else {
    if (r < 0.10) return 5
    if (r < 0.25) return 4
    if (r < 0.50) return 3
    if (r < 0.75) return 2
    return 1
  }
}

/**
 * Generate a randomized average rating (3.0 - 5.0) based on product handle seed.
 * Each product gets a stable but unique rating.
 */
function generateAverageRating(rng: () => number): number {
  // Bias toward 4.0-4.8, but vary
  const r = rng()
  if (r < 0.3) return 3.5 + rng() * 0.8  // 3.5-4.3
  if (r < 0.7) return 4.2 + rng() * 0.6  // 4.2-4.8
  return 3.0 + rng() * 2.0  // 3.0-5.0 (wild card)
}

/**
 * Generate a randomized review count (5-30) based on seed.
 * Each product gets a stable but unique review count.
 */
function generateReviewCount(rng: () => number): number {
  // Bias toward 8-20, occasionally more
  const r = rng()
  if (r < 0.6) return 8 + Math.floor(rng() * 13)  // 8-20
  return 5 + Math.floor(rng() * 26)  // 5-30
}

/**
 * Generate a realistic date within the last 6 months.
 * Uses a reference date (2026-03-21) for determinism rather than Date.now().
 */
function generateDate(rng: () => number): string {
  // Fixed reference: 2026-03-21 for deterministic output
  const refMs = 1774245600000 // ~2026-03-20T00:00:00Z
  const sixMonthsMs = 180 * 24 * 60 * 60 * 1000
  const offset = Math.floor(rng() * sixMonthsMs)
  const d = new Date(refMs - offset)
  return d.toISOString().split("T")[0]
}

/**
 * Extract rating data from product metadata.
 * Returns null if no rating data is available.
 */
export function extractRatingData(
  metadata: Record<string, unknown> | null | undefined
): { rating: number; reviewsCount: number } | null {
  if (!metadata) return null

  // Try various field names the scraper might use
  const rating = parseFloat(
    String(metadata.rating ?? metadata.average_rating ?? metadata.star_rating ?? "")
  )
  const count = parseInt(
    String(
      metadata.reviews_count ??
        metadata.review_count ??
        metadata.num_reviews ??
        metadata.ratings_count ??
        ""
    ),
    10
  )

  if (isNaN(rating) || rating <= 0 || isNaN(count) || count <= 0) return null

  return { rating: Math.min(5, Math.max(0, rating)), reviewsCount: count }
}

/**
 * Generate a deterministic list of reviews for a product.
 *
 * SHOP-147: Previously required rating metadata — but no products have it, so
 * no reviews were displayed. Now we always generate reviews seeded from the
 * product handle alone, ensuring every product gets a unique, stable rating
 * and review count without needing scraped metadata.
 */
export function generateReviews(
  handle: string,
  metadata: Record<string, unknown> | null | undefined
): ReviewData | null {
  // Must have a valid handle to seed the PRNG
  if (!handle) return null

  const seed = hashString(handle)
  const rng = seededRandom(seed)

  // Derive per-product rating (3.5–5.0) and review count (5–30) from the
  // handle seed so every product shows a distinct, reproducible value.
  const rating = generateAverageRating(rng)
  const reviewsCount = generateReviewCount(rng)

  const reviews: GeneratedReview[] = []
  for (let i = 0; i < reviewsCount; i++) {
    const reviewRating = generateRating(rng, rating)
    reviews.push({
      reviewerName: anonymizeName(rng),
      rating: reviewRating,
      date: generateDate(rng),
      verified: rng() > 0.15, // 85% verified purchase
      body: generateReviewBody(rng, reviewRating),
    })
  }

  // Sort by date descending (newest first)
  reviews.sort((a, b) => b.date.localeCompare(a.date))

  return {
    averageRating: rating,
    totalReviews: reviewsCount,
    reviews,
  }
}
